import React, { ReactNode } from 'react'
import { ChipTypeMap, Divider, styled, unstable_composeClasses } from '@mui/material'
import classnames from 'classnames'

import AutocompleteField from '../AutocompleteField/AutocompleteField'
import { AutocompleteFieldProps, AutocompleteOption } from '../AutocompleteField/AutocompleteField.types'
import autocompleteFieldClasses from '../AutocompleteField/autoCompleteFieldClasses'
import { getGlobalSearchFieldUtilityClass, GlobalSearchFieldClasses } from './globalSearchFieldClasses'

export interface GlobalSearchFieldProps<
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
> extends AutocompleteFieldProps<
    Option,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent,
    QueryVariables,
    MoreQueryVariables,
    QueryResults,
    MoreQueryResults
  > {
  autocompleteClassName?: string
  classes?: Partial<GlobalSearchFieldClasses>
  rightActions?: React.ReactNode
}

const GlobalSearchFieldRoot = styled('div', {
  name: 'GlobalSearchField',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
  width: '100%',
  background: theme.colours.new.white,
  borderRadius: theme.display.borderRadius,
  display: 'flex',
  alignItems: 'center',
}))

const GlobalSearchFieldRightActions = styled('div', {
  name: 'GlobalSearchField',
  slot: 'RightActions',
  overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
  padding: theme.spacing(2),
}))

const GlobalSearchFieldAutocomplete = styled(AutocompleteField, {
  name: 'GlobalSearchField',
  slot: 'Autocomplete',
  overridesResolver: (_, styles) => styles.autocomplete,
})<{ rightActions?: ReactNode }>(({ theme, rightActions }) => ({
  [`> .${autocompleteFieldClasses.field}`]: {
    width: '100%',
    padding: theme.spacing(2),
    background: theme.colours.new.white,
    borderRadius: rightActions
      ? `${theme.display.borderRadius} 0 0 ${theme.display.borderRadius}`
      : theme.display.borderRadius,
    [`> .${autocompleteFieldClasses.searchIcon}`]: {
      width: '20px',
      height: '20px',
    },
    '> .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  [`& .${autocompleteFieldClasses.field} .${autocompleteFieldClasses.select}`]: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(18),
  },
  [`& .${autocompleteFieldClasses.endAdornment}`]: {
    '> button': {
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(18),
      marginRight: theme.spacing(2),
    },
  },
})) as typeof AutocompleteField

const useUtilityClasses = <
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  ownerState: Partial<
    GlobalSearchFieldProps<
      Option,
      Multiple,
      DisableClearable,
      FreeSolo,
      any,
      QueryVariables,
      MoreQueryVariables,
      QueryResults,
      MoreQueryResults
    >
  >,
) => {
  const slots = {
    root: ['root'],
    autocomplete: ['autocomplete'],
    rightActions: ['rightActions'],
  }

  return unstable_composeClasses(slots, getGlobalSearchFieldUtilityClass, ownerState.classes)
}

const _GlobalSearchField = function _GlobalSearchField<
  Option extends AutocompleteOption = AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = true,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  props: GlobalSearchFieldProps<
    Option,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent,
    QueryVariables,
    MoreQueryVariables,
    QueryResults,
    MoreQueryResults
  >,
) {
  const { className, classes, rightActions, ...rootProps } = props

  const slotClasses = useUtilityClasses({ classes, rightActions })

  return (
    <GlobalSearchFieldRoot className={classnames(slotClasses.root, className)}>
      <GlobalSearchFieldAutocomplete
        asyncMode="search"
        slotProps={{
          popper: {
            placement: 'bottom-start',
            // setting the width with sx doesn't fully work so we need to set it here
            style: { maxWidth: '928px', width: '100%' },
          },
          paper: {
            sx(theme) {
              return {
                height: 'auto', // Allow it to shrink
                maxHeight: '60vh', // Cap at 60vh
                marginTop: theme.spacing(2),
                [`& .${autocompleteFieldClasses.list}`]: {
                  padding: theme.spacing(2),
                  maxHeight: '60vh', // Prevents it from growing beyond
                },
              }
            },
          },
        }}
        className={classnames(slotClasses.autocomplete, className)}
        {...rootProps}
      />
      {rightActions && (
        <>
          <Divider orientation="vertical" flexItem />
          <GlobalSearchFieldRightActions className={classnames(slotClasses.rightActions)}>
            {rightActions}
          </GlobalSearchFieldRightActions>
        </>
      )}
    </GlobalSearchFieldRoot>
  )
}

export const GlobalSearchField = React.memo(_GlobalSearchField) as typeof _GlobalSearchField

export default GlobalSearchField
